import { useId, useState, type ReactNode } from "react";

/**
 * glowin's icon set. Drawn for this product, inline SVG only: no icon font, no image files, no third-party set.
 *
 * KEPT IDENTICAL IN BOTH CONSOLES (web/restaurant-console and web/admin-console share no UI package);
 * shared-files-identical.test.ts fails if the two copies differ.
 *
 * One grid for all of them: 24×24 viewBox, 2px stroke, round caps and joins, no fill. They take the text colour
 * (currentColor), so an icon is the colour of whatever it sits in: a badge, a button, muted text.
 *
 * Decorative by default (aria-hidden), because nearly every icon sits next to a word or inside a labelled
 * button. Pass `title` when the icon stands alone and IS the label.
 */

export interface IconProps {
  /** Pixels. 18 inline with body text, 16 in dense rows, 20+ for page-level marks. */
  size?: number;
  /** Makes the icon announce itself. Only for an icon with no visible label beside it. */
  title?: string;
  className?: string;
}

function Svg({ size = 18, title, className, children }: IconProps & { children: ReactNode }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className ? `icon ${className}` : "icon"}
      {...(title ? { role: "img", "aria-label": title } : { "aria-hidden": true, focusable: "false" })}
    >
      {children}
    </svg>
  );
}

const PHONE = "M5 4h3l2 5-2.5 1.5a11 11 0 0 0 6 6L15 14l5 2v3a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2Z";

// --- Sections -------------------------------------------------------------------------------------------

/** Orders: a till receipt. */
export const ReceiptIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M6 3h12v18l-3-2-3 2-3-2-3 2Z" />
    <path d="M9 8h6M9 12h6M9 16h3" />
  </Svg>
);

/** Menu: an open menu card. */
export const MenuIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M12 6c-2-1.5-5-2-8-2v14c3 0 6 .5 8 2" />
    <path d="M12 6c2-1.5 5-2 8-2v14c-3 0-6 .5-8 2Z" />
  </Svg>
);

export const PhoneIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d={PHONE} />
  </Svg>
);

export const ClockIcon = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 2" />
  </Svg>
);

/** Restaurant info: a shopfront with an awning. */
export const StoreIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M4.5 4h15L21 9H3Z" />
    <path d="M3 9a3 3 0 0 0 6 0 3 3 0 0 0 6 0 3 3 0 0 0 6 0" />
    <path d="M5 12v8h14v-8" />
    <path d="M10 20v-5h4v5" />
  </Svg>
);

/** Settings: three sliders. */
export const SlidersIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M4 6h9M17 6h3M4 12h3M11 12h9M4 18h11M19 18h1" />
    <circle cx="15" cy="6" r="2" />
    <circle cx="9" cy="12" r="2" />
    <circle cx="17" cy="18" r="2" />
  </Svg>
);

// --- Call outcomes --------------------------------------------------------------------------------------

/** The caller ordered. */
export const OrderPlacedIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M5 8h14l-1 12H6Z" />
    <path d="M9 8V6a3 3 0 0 1 6 0v2" />
    <path d="m9.5 14 2 2 3.5-3.5" />
  </Svg>
);

/** The caller hung up without ordering: a handset put down. */
export const HangUpIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M2.5 14.5c5.3-4.7 13.7-4.7 19 0l-2.3 2.8-3.7-1.4v-2.6a10.5 10.5 0 0 0-7 0v2.6l-3.7 1.4Z" />
  </Svg>
);

/** Handed to a person: a handset with an outgoing arrow. */
export const TransferIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d={PHONE} />
    <path d="M16 3h5v5M21 3l-5 5" />
  </Svg>
);

export const AlertIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M12 3.5 2.5 20h19Z" />
    <path d="M12 10v4M12 17h.01" />
  </Svg>
);

/** The icon for a call outcome, by the API's name for it. Unknown outcomes get the phone. */
export function CallOutcomeIcon({ outcome, ...p }: IconProps & { outcome: string }) {
  if (outcome === "ORDER_PLACED") return <OrderPlacedIcon {...p} />;
  if (outcome === "RESERVED") return <CalendarIcon {...p} />;
  if (outcome === "ABANDONED") return <HangUpIcon {...p} />;
  if (outcome === "TRANSFERRED") return <TransferIcon {...p} />;
  if (outcome === "FAILED") return <AlertIcon {...p} />;
  return <PhoneIcon {...p} />;
}

// --- Facts ----------------------------------------------------------------------------------------------

/** Money: a coin. */
export const CoinIcon = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="M14.5 9.2c-.4-.8-1.3-1.2-2.5-1.2-1.4 0-2.5.7-2.5 1.9s1.1 1.6 2.5 1.9 2.5.8 2.5 2-1.1 2-2.5 2c-1.2 0-2.1-.5-2.5-1.3M12 6.5V8M12 16v1.5" />
  </Svg>
);

/** Billed time: a stopwatch. */
export const TimerIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M10 2.5h4M12 2.5V5" />
    <circle cx="12" cy="13.5" r="7.5" />
    <path d="M12 10v3.5l2.5 1.5" />
  </Svg>
);

export const UserIcon = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="12" cy="8" r="4" />
    <path d="M4 21a8 8 0 0 1 16 0" />
  </Svg>
);

export const UsersIcon = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="9" cy="8" r="3.5" />
    <path d="M2.5 20a6.5 6.5 0 0 1 13 0" />
    <path d="M16 4.5a3.5 3.5 0 0 1 0 7M18 14.5c2 .9 3.5 3 3.5 5.5" />
  </Svg>
);

/** An order's contents: a shopping bag. */
export const BagIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M5 8h14l-1 12H6Z" />
    <path d="M9 8V6a3 3 0 0 1 6 0v2" />
  </Svg>
);

export const CalendarIcon = (p: IconProps) => (
  <Svg {...p}>
    <rect x="3" y="5" width="18" height="16" rx="2" />
    <path d="M3 10h18M8 3v4M16 3v4" />
  </Svg>
);

export const MessageIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M4 5h16v11H9l-5 4Z" />
  </Svg>
);

export const MailIcon = (p: IconProps) => (
  <Svg {...p}>
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="m3 7 9 6 9-6" />
  </Svg>
);

export const PinIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M12 21s-7-6.2-7-11a7 7 0 0 1 14 0c0 4.8-7 11-7 11Z" />
    <circle cx="12" cy="10" r="2.5" />
  </Svg>
);

export const GlobeIcon = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="M3 12h18M12 3c2.5 2.5 3.8 5.5 3.8 9s-1.3 6.5-3.8 9c-2.5-2.5-3.8-5.5-3.8-9S9.5 5.5 12 3Z" />
  </Svg>
);

/** The agent's voice: a waveform. */
export const VoiceIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M4 10v4M8 7v10M12 4v16M16 7v10M20 10v4" />
  </Svg>
);

/** A recording. */
export const RecordIcon = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="9" />
    <circle cx="12" cy="12" r="3.5" fill="currentColor" />
  </Svg>
);

export const FileIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M6 3h8l4 4v14H6Z" />
    <path d="M14 3v4h4" />
  </Svg>
);

export const CardIcon = (p: IconProps) => (
  <Svg {...p}>
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="M3 10h18M7 15h3" />
  </Svg>
);

/** Not available / sold out. */
export const BanIcon = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="m5.6 5.6 12.8 12.8" />
  </Svg>
);

/** Preparing: a flame. */
export const FlameIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M12 21c-3.9 0-7-2.7-7-6.5 0-3 2-5.2 3.5-7 .2 2 1.2 3.2 2.5 3.5C11 7.5 12.5 5 14.5 3c.4 3 4.5 5.5 4.5 11.5 0 3.8-3.1 6.5-7 6.5Z" />
  </Svg>
);

/** Ready for pickup: a service bell. */
export const BellIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M4 18h16M5.5 18a6.5 6.5 0 0 1 13 0M12 8.5V6M10 6h4" />
  </Svg>
);

export const InfoIcon = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 11v5M12 8h.01" />
  </Svg>
);

// --- Actions --------------------------------------------------------------------------------------------

export const CheckIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="m5 12.5 4.5 4.5L19 7" />
  </Svg>
);

export const CheckCircleIcon = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="m8 12.5 2.5 2.5L16 9.5" />
  </Svg>
);

export const PlusIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M12 5v14M5 12h14" />
  </Svg>
);

export const TrashIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M4 7h16M10 11v6M14 11v6M6 7l1 13h10l1-13M9 7V4h6v3" />
  </Svg>
);

export const PencilIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M4 20h4L19 9l-4-4L4 16Z" />
    <path d="m13.5 6.5 4 4" />
  </Svg>
);

/** Publish: send the draft live. */
export const SendIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M4 12 20 4l-4 16-4-6Z" />
    <path d="m12 14 8-10" />
  </Svg>
);

export const UploadIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M12 15V4M7 9l5-5 5 5" />
    <path d="M4 15v4a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-4" />
  </Svg>
);

export const CloseIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M6 6l12 12M18 6 6 18" />
  </Svg>
);

export const ArrowLeftIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M19 12H5M11 6l-6 6 6 6" />
  </Svg>
);

export const ArrowRightIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M5 12h14M13 6l6 6-6 6" />
  </Svg>
);

export const ChevronLeftIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="m15 6-6 6 6 6" />
  </Svg>
);

export const ChevronRightIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="m9 6 6 6-6 6" />
  </Svg>
);

export const ChevronDownIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="m6 9 6 6 6-6" />
  </Svg>
);

export const CopyIcon = (p: IconProps) => (
  <Svg {...p}>
    <rect x="8" y="8" width="12" height="12" rx="2" />
    <path d="M16 8V5a1 1 0 0 0-1-1H5a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h3" />
  </Svg>
);

export const SignOutIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M15 4h4v16h-4M10 8l-4 4 4 4M6 12h10" />
  </Svg>
);

export const SignInIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M15 4h4v16h-4M10 8l4 4-4 4M14 12H4" />
  </Svg>
);

export const SearchIcon = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="11" cy="11" r="7" />
    <path d="m20 20-4-4" />
  </Svg>
);

export const RefreshIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M21 12a9 9 0 1 1-3.5-7.1" />
    <path d="M21 3v6h-6" />
  </Svg>
);

export const ExternalIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M14 4h6v6M20 4l-9 9" />
    <path d="M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5" />
  </Svg>
);

export const ExpandIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5" />
  </Svg>
);

export const CollapseIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M9 4v5H4M15 4v5h5M9 20v-5H4M15 20v-5h5" />
  </Svg>
);

export const SpeakerOnIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M11 5 6 9H3v6h3l5 4Z" />
    <path d="M15.5 8.5a5 5 0 0 1 0 7M18.5 5.5a9 9 0 0 1 0 13" />
  </Svg>
);

export const SpeakerOffIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M11 5 6 9H3v6h3l5 4Z" />
    <path d="m16 9 5 6M21 9l-5 6" />
  </Svg>
);

// --- Hint -----------------------------------------------------------------------------------------------

/**
 * Explanation behind a small info icon, instead of a paragraph under every field.
 *
 * Shown on hover or keyboard focus, and toggled by tap for touch screens. The text stays in the page (and is
 * announced through aria-describedby), it is only out of the way until wanted.
 *
 * A focusable SPAN, deliberately not a <button>: hints sit inside <label> elements, where a button would become
 * the label's control and detach the input it names. The click is prevented so tapping the icon does not tick
 * the checkbox its label belongs to.
 */
export function Hint({ children, label = "More info" }: { children: ReactNode; label?: string }) {
  const [open, setOpen] = useState(false);
  const id = useId();
  const toggle = (e: { preventDefault(): void; stopPropagation(): void }) => {
    e.preventDefault();
    e.stopPropagation();
    setOpen((o) => !o);
  };
  return (
    <span className={open ? "hint open" : "hint"}>
      <span
        role="button"
        tabIndex={0}
        className="hint-trigger"
        aria-label={label}
        aria-expanded={open}
        aria-describedby={id}
        onClick={toggle}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") toggle(e);
          if (e.key === "Escape") setOpen(false);
        }}
        onBlur={() => setOpen(false)}
      >
        <InfoIcon size={15} />
      </span>
      <span role="tooltip" id={id} className="hint-text">
        {children}
      </span>
    </span>
  );
}

/** An icon and a short label side by side, aligned. The common case for buttons, badges and facts. */
export function WithIcon({ icon, children }: { icon: ReactNode; children: ReactNode }) {
  return (
    <span className="with-icon">
      {icon}
      <span>{children}</span>
    </span>
  );
}
